import { Box, Stack, Typography, useTheme } from "@mui/material";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import {
  REASSIST_ARCHIVE_PATH,
  REASSIST_LABELS,
  REASSIST_REQUEST_PATH,
  REASSIST_STATUS_PATH,
} from "../config";
import { useReassistArchiveEnabled } from "../hooks/useReassistArchiveEnabled";
import { cardShadow, TEXT } from "../utils/tokens";
import {
  ReassistArchiveButton,
  ReassistButton,
  ReassistRequestButton,
} from "./ReassistButton";
import { REASSIST_LOGO_HEIGHT, ReassistLogo } from "./ReassistLogo";

/**
 * マイページの R.E.ASSIST のサービスカード。全幅の白い面（影は MenuButton と同じ、角丸8、枠なし）。
 * PC は左右2列（左=ロゴ・見出し・補足、右=「依頼状況・請求」「役所資料を探す」（副）と「役所資料を依頼する」（主））。
 * SP は縦1列で、ボタンは幅いっぱいに主→副の順で積む。
 */
export const ReassistServiceCard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const archiveEnabled = useReassistArchiveEnabled();

  const status = (
    <ReassistButton
      label={REASSIST_LABELS.status}
      variant="outlined"
      to={REASSIST_STATUS_PATH}
      startIcon={<ReceiptLongOutlined />}
      size="card"
      fullWidth={isMobile}
    />
  );
  const archive = archiveEnabled ? (
    <ReassistArchiveButton
      to={REASSIST_ARCHIVE_PATH}
      size="card"
      fullWidth={isMobile}
    />
  ) : null;
  const request = (
    <ReassistRequestButton
      to={REASSIST_REQUEST_PATH}
      size="card"
      fullWidth={isMobile}
    />
  );

  return (
    <Box
      component="section"
      aria-label={REASSIST_LABELS.service}
      sx={{
        boxSizing: "border-box",
        width: "100%",
        backgroundColor: theme.palette.white.main,
        boxShadow: cardShadow(theme),
        borderRadius: "8px",
        px: { xs: 2.5, [MOBILE_BREAKPOINT]: 4 },
        py: { xs: 2.5, [MOBILE_BREAKPOINT]: 3 },
        display: "flex",
        flexDirection: { xs: "column", [MOBILE_BREAKPOINT]: "row" },
        alignItems: { xs: "stretch", [MOBILE_BREAKPOINT]: "center" },
        gap: { xs: 2, [MOBILE_BREAKPOINT]: 3 },
      }}
    >
      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
        <ReassistLogo height={REASSIST_LOGO_HEIGHT.serviceCard} />
        <Typography sx={{ ...TEXT.cardTitle, mt: 2.5, color: "text.primary" }}>
          建築計画概要書や道路台帳など、役所の資料の取得を代行します
        </Typography>
        <Typography sx={{ ...TEXT.body, mt: 0.5, color: "text.secondary" }}>
          依頼の進み具合、納品された資料、請求額を確認できます
        </Typography>
      </Box>
      {isMobile ? (
        <Stack gap={1}>
          {request}
          {archive}
          {status}
        </Stack>
      ) : (
        <Stack direction="row" gap={1.5} sx={{ flexShrink: 0 }}>
          {status}
          {archive}
          {request}
        </Stack>
      )}
    </Box>
  );
};
